import React, { useEffect, useMemo, useState } from 'react';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import CountryLink from 'Components/common/CountryLink';
import LoaderPanel from 'Components/common/LoaderPanel';
import VaccinePaceChart from '@reuters-graphics/chart-module-vaccine-pace-chart/dist/index.js';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';
import fetchJson from 'Utils/fetchJson';
import last from 'lodash/last';
import maxBy from 'lodash/maxBy';
import useObservable from 'Components/hooks/observed';
import useWindowDimensions from 'Components/hooks/useWindowDimensions';

const atlas = new AtlasMetadataClient();

const Pace = (props) => {
  const chartContainer = React.createRef();
  const { width } = useWindowDimensions();
  const isVisible = useObservable({ ref: chartContainer, rootMargin: '600px' });
  const [data, setData] = useState(null);
  const [max, setMax] = useState(null);
  const [latestMax, setLatestMax] = useState(null);

  const chart = useMemo(() => new VaccinePaceChart(), []);

  const resize = debounce(() => {
    chart.draw();
  }, 250);

  useEffect(() => {
    if (data || !isVisible) return;
    fetchJson('https://graphics.thomsonreuters.com/data/2020/coronavirus/owid-covid-vaccinations/rolling-averages-by-country.json')
      .then(data => {
        const countryStats = Object.keys(data)
          .filter(isoAlpha2 => {
            const country = atlas.getCountry(isoAlpha2);
            if (!country.dataProfile.population) return false;
            return country.dataProfile.population.d > 1000000;
          })
          .map((isoAlpha2) => {
            const max = Math.max(...data[isoAlpha2]);
            const latest = last(data[isoAlpha2]);
            return {
              isoAlpha2,
              max,
              latest,
            };
          });

        const latestMax = maxBy(countryStats, d => d.latest);
        const max = maxBy(countryStats, d => d.max);

        setMax(max);
        setLatestMax(latestMax);
        setData(data);
      });
  }, [data, isVisible]);

  useEffect(() => {
    if (!data) return;
    chart
      .selection(chartContainer.current)
      .props({
        highlight: '#A3BE8C',
        margin: {
          top: 10,
          right: width > 900 ? 140 : 120,
          bottom: 40,
          left: 10,
        },
        aspectHeight: [
          { breakpoint: 600, ratio: 0.5 },
          { breakpoint: 500, ratio: 0.75 },
          { breakpoint: 450, ratio: 1.0 },
          { breakpoint: 0, ratio: 1.15 },
        ],
        keyOffsets: {
          x: 0,
          y: width <= 375 ? -45 : 0,
        },
      })
      .data(data)
      .draw();
  }, [data, chart, chartContainer]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return (
    <div className={classnames(component)}>
      <section>
        <h4>How fast are countries vaccinating?</h4>
        {(max && latestMax && max.isoAlpha2 !== latestMax.isoAlpha2) && (
          <p>Among countries with more than 1 million people, <CountryLink country={latestMax.isoAlpha2} /> is currently vaccinating faster than any other, administering a daily average of  <strong>{Math.floor(latestMax.latest).toLocaleString('en')} doses</strong> per 100,000 people in the past seven days. <CountryLink country={max.isoAlpha2} /> was previously the fastest, administering about <strong>{Math.floor(max.max).toLocaleString('en')} doses</strong> per 100,000 people per day.</p>
        )}
        {(max && latestMax && max.isoAlpha2 === latestMax.isoAlpha2) && (
          <p>Among countries with more than 1 million people, <CountryLink country={latestMax.isoAlpha2} /> is currently vaccinating faster than any other, administering a daily average of <strong>{Math.floor(latestMax.latest).toLocaleString('en')} doses per 100,000 people</strong> in the past seven days. Their fastest was about <strong>{Math.floor(max.max).toLocaleString('en')} doses</strong> per 100,000 people per day.</p>
        )}

      </section>

      <section className='pace-chart-container'>
        <div className='vaccination-pace-chart' ref={chartContainer} />
        {!data && (
          <LoaderPanel height={620} />
        )}
        {
          data && (
            <p className='footnote'>Note: The 7-day average is calculated using daily figures from <a href='https://github.com/owid/covid-19-data/tree/master/public/data/vaccinations#vaccination-data' target='_blank' rel='noreferrer'>Our World in Data</a>. Not all countries report vaccinations daily, so daily figures are <a href='https://github.com/owid/covid-19-data/issues/333#issuecomment-763015298' target='_blank' rel='noreferrer'>estimated</a> whenever a country reports the total number of vaccinations they have administered.</p>
          )
        }
      </section>
    </div>
  );
};

export default Pace;
